import { useEffect, useRef, useState } from 'react';
import { getApiToken, setApiToken } from '../lib/api.js';

export function SettingsPage(): JSX.Element {
  const [token, setToken] = useState<string>(getApiToken());
  const [saved, setSaved] = useState(false);
  const [health, setHealth] = useState<{ kind: 'idle' } | { kind: 'ok' } | { kind: 'err'; msg: string }>(
    { kind: 'idle' },
  );

  // Server-Info: Backend per Health-Check live prüfen (gleiche Origin, Proxy).
  useEffect(() => {
    let alive = true;
    const check = async (): Promise<void> => {
      try {
        const res = await fetch('/health', { cache: 'no-store' });
        if (!alive) return;
        setHealth(res.ok ? { kind: 'ok' } : { kind: 'err', msg: `HTTP ${res.status}` });
      } catch (err) {
        if (alive) setHealth({ kind: 'err', msg: (err as Error).message });
      }
    };
    void check();
    const t = window.setInterval(() => void check(), 5000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  const backendOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const [importStatus, setImportStatus] = useState<
    { kind: 'idle' } | { kind: 'busy' } | { kind: 'ok'; msg: string } | { kind: 'err'; msg: string }
  >({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onSaveToken = (): void => {
    setApiToken(token.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const onExport = async (): Promise<void> => {
    const headers: Record<string, string> = {};
    const t = getApiToken();
    if (t) headers.authorization = `Bearer ${t}`;
    const res = await fetch('/api/v1/config/export', { headers });
    if (!res.ok) {
      alert(`Export fehlgeschlagen: HTTP ${res.status}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `livepuls-config-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onPickImport = (): void => {
    fileInputRef.current?.click();
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset, damit derselbe File nochmal getriggert werden kann
    if (!file) return;

    const ok = window.confirm(
      'Achtung: Beim Import werden ALLE bestehenden Panels, Buttons, Partylines, ' +
        'Geräte und Gruppen ersetzt. Vorher unbedingt einen Export machen.\n\nFortfahren?',
    );
    if (!ok) return;

    setImportStatus({ kind: 'busy' });
    try {
      const text = await file.text();
      const payload = JSON.parse(text) as unknown;
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      const t = getApiToken();
      if (t) headers.authorization = `Bearer ${t}`;
      const res = await fetch('/api/v1/config/import', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      setImportStatus({ kind: 'ok', msg: 'Import erfolgreich. Seite wird neu geladen …' });
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setImportStatus({ kind: 'err', msg });
    }
  };

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-text">Einstellungen</h1>
        <p className="text-sm text-text-dim mt-1">
          API-Token, Config-Export/Import und Server-Info.
        </p>
      </header>

      <section className="card p-5 space-y-3">
        <div className="label-sm">Server-Info</div>
        <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 text-sm">
          <div className="text-text-mute">Backend</div>
          <div className="text-text break-all">{backendOrigin || '–'}</div>
          <div className="text-text-mute">API-Version</div>
          <div className="text-text">v1</div>
          <div className="text-text-mute">Status</div>
          <div>
            {health.kind === 'ok' ? (
              <span className="text-green-400">● erreichbar</span>
            ) : health.kind === 'err' ? (
              <span className="text-red-400">● nicht erreichbar ({health.msg})</span>
            ) : (
              <span className="text-text-mute">prüfe …</span>
            )}
          </div>
        </div>
      </section>

      <section className="card p-5 space-y-3">
        <div className="label-sm">API-Token (Bearer)</div>
        <p className="text-xs text-text-mute">
          Wird für alle <code>/api/v1/*</code>-Aufrufe als
          <code> Authorization: Bearer &lt;token&gt; </code> mitgeschickt.
          Leerlassen, wenn das Backend keinen Token verlangt (Dev-Default).
        </p>
        <div className="flex gap-2">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="<token>"
            className="flex-1 px-2 py-1 rounded bg-surface-2 text-text text-sm"
          />
          <button
            onClick={onSaveToken}
            className="px-3 py-1 rounded bg-accent text-bg text-sm"
          >
            Speichern
          </button>
        </div>
        {saved && <div className="text-xs text-green-400">Gespeichert.</div>}
      </section>

      <section className="card p-5 space-y-3">
        <div className="label-sm">Konfigurations-Export</div>
        <p className="text-xs text-text-mute">
          Lädt den aktuellen Draft-Snapshot (Panels, Buttons, Partylines,
          Geräte) als JSON herunter – z. B. für Backup oder Migration.
        </p>
        <button
          onClick={() => void onExport()}
          className="px-3 py-1 rounded bg-surface-2 hover:bg-surface-3 text-text text-sm"
        >
          Config exportieren
        </button>
      </section>

      <section className="card p-5 space-y-3">
        <div className="label-sm">Konfigurations-Import</div>
        <p className="text-xs text-text-mute">
          Lädt einen zuvor exportierten Snapshot. <strong className="text-red-400">Ersetzt
          komplett</strong> alle bestehenden Panels, Buttons, Partylines, Geräte
          und Gruppen. Externe Ports (Laufzeitdaten) bleiben unverändert.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={(e) => void onImportFile(e)}
          className="hidden"
        />
        <button
          onClick={onPickImport}
          disabled={importStatus.kind === 'busy'}
          className="px-3 py-1 rounded bg-surface-2 hover:bg-surface-3 text-text text-sm disabled:opacity-50"
        >
          {importStatus.kind === 'busy' ? 'Importiere …' : 'JSON-Datei wählen …'}
        </button>
        {importStatus.kind === 'ok' && (
          <div className="text-xs text-green-400">{importStatus.msg}</div>
        )}
        {importStatus.kind === 'err' && (
          <div className="text-xs text-red-400">Fehler: {importStatus.msg}</div>
        )}
      </section>
    </div>
  );
}
